/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import Topbar from "@/components/Topbar";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  User, Buildings, Lock, Check, Spinner, Eye, EyeSlash,
  Receipt, ArrowsClockwise, ShieldCheck, WarningCircle,
} from "@phosphor-icons/react";

const TABS = [
  { id: "perfil", label: "Perfil", icon: User },
  { id: "empresa", label: "Empresa", icon: Buildings },
  { id: "fiscal", label: "Documentos Fiscais", icon: Receipt },
  { id: "seguranca", label: "Segurança", icon: Lock },
];

export default function Settings() {
  const { user, tenant, refresh, setTenant } = useAuth();
  const [activeTab, setActiveTab] = useState("perfil");

  const onTenantSaved = (updated) => {
    if (updated) setTenant(updated);
  };

  return (
    <>
      <Topbar
        title="Minha conta"
        subtitle="Seus dados, da sua empresa e segurança em um único lugar."
      />
      <div className="p-6 md:p-8 max-w-4xl" data-testid="settings-page">
        <div className="flex gap-1 mb-6 border-b border-slate-200" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(t.id)}
                data-testid={`settings-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  active
                    ? "border-slate-900 text-slate-900"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                <Icon size={14} weight="regular" />
                {t.label}
              </button>
            );
          })}
        </div>

        {activeTab === "perfil" && <ProfileTab user={user} onSaved={refresh} />}
        {activeTab === "empresa" && <CompanyTab tenant={tenant} onSaved={onTenantSaved} />}
        {activeTab === "fiscal" && <FiscalTab tenant={tenant} onSaved={onTenantSaved} />}
        {activeTab === "seguranca" && <SecurityTab />}
      </div>
    </>
  );
}

// ─── Tab: Perfil ──────────────────────────────────────────────────

function ProfileTab({ user, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setName(user?.name || ""); }, [user]);

  const save = async () => {
    if (!name.trim() || name.trim() === user?.name) return;
    setSaving(true);
    try {
      await api.patch("/auth/me", { name: name.trim() });
      toast.success("Perfil atualizado.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Perfil" desc="Seus dados pessoais como dono da operação.">
      <FormRow label="Seu nome">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="settings-profile-name-input"
          className="settings-input"
          placeholder="Como devo te chamar"
        />
      </FormRow>
      <FormRow label="Email">
        <input
          type="email"
          value={user?.email || ""}
          disabled
          data-testid="settings-profile-email-input"
          className="settings-input bg-slate-50 text-slate-500 cursor-not-allowed"
        />
        <p className="text-[11px] text-slate-400 mt-1">
          Para trocar o email, fale com o suporte AgentUS.
        </p>
      </FormRow>
      <div className="flex justify-end pt-2">
        <SaveButton
          onClick={save}
          loading={saving}
          disabled={!name.trim() || name.trim() === user?.name}
          testid="settings-profile-save-btn"
        />
      </div>
    </Card>
  );
}

// ─── Tab: Empresa ─────────────────────────────────────────────────

function CompanyTab({ tenant, onSaved }) {
  const [form, setForm] = useState({
    name: tenant?.name || "",
    cnpj: tenant?.cnpj || "",
    phone: tenant?.phone || "",
    address: tenant?.address || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      name: tenant?.name || "",
      cnpj: tenant?.cnpj || "",
      phone: tenant?.phone || "",
      address: tenant?.address || "",
    });
  }, [tenant]);

  const dirty =
    form.name !== (tenant?.name || "") ||
    form.cnpj !== (tenant?.cnpj || "") ||
    form.phone !== (tenant?.phone || "") ||
    form.address !== (tenant?.address || "");

  const save = async () => {
    if (!dirty) return;
    if (!form.name.trim()) {
      toast.error("O nome da empresa não pode ficar vazio.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me/tenant", form);
      onSaved?.(data);
      toast.success("Empresa atualizada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Card title="Empresa" desc="Dados da oficina / concessionária que o AgentUS representa.">
      <FormRow label="Nome da empresa">
        <input
          type="text" value={form.name}
          onChange={(e) => set("name", e.target.value)}
          data-testid="settings-company-name-input"
          className="settings-input"
          placeholder="Ex: Oficina Bom Caminho"
        />
      </FormRow>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormRow label="CNPJ">
          <input
            type="text" value={form.cnpj}
            onChange={(e) => set("cnpj", e.target.value)}
            data-testid="settings-company-cnpj-input"
            className="settings-input"
            placeholder="00.000.000/0000-00"
          />
        </FormRow>
        <FormRow label="Telefone comercial">
          <input
            type="text" value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            data-testid="settings-company-phone-input"
            className="settings-input"
            placeholder="(11) 99999-9999"
          />
        </FormRow>
      </div>
      <FormRow label="Endereço">
        <input
          type="text" value={form.address}
          onChange={(e) => set("address", e.target.value)}
          data-testid="settings-company-address-input"
          className="settings-input"
          placeholder="Rua, número, bairro, cidade"
        />
      </FormRow>
      <div className="flex justify-end pt-2">
        <SaveButton
          onClick={save} loading={saving} disabled={!dirty}
          testid="settings-company-save-btn"
        />
      </div>
    </Card>
  );
}

// ─── Tab: Segurança ───────────────────────────────────────────────

function SecurityTab() {
  const [cur, setCur] = useState("");
  const [neu, setNeu] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setCur(""); setNeu(""); setConfirm(""); };

  const save = async () => {
    if (neu.length < 8) { toast.error("A nova senha precisa ter pelo menos 8 caracteres."); return; }
    if (neu !== confirm) { toast.error("As senhas novas não coincidem."); return; }
    if (neu === cur) { toast.error("A nova senha precisa ser diferente da atual."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cur, new_password: neu,
      });
      toast.success("Senha alterada com sucesso. Você está protegido. 🔒");
      reset();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (msg === "current password incorrect") {
        toast.error("Senha atual incorreta.");
      } else {
        toast.error(msg || "Não foi possível alterar a senha.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Segurança" desc="Mantenha sua conta segura — troque sua senha regularmente.">
      <FormRow label="Senha atual">
        <PasswordInput
          value={cur} onChange={setCur} show={showCur} setShow={setShowCur}
          testid="settings-pw-current"
        />
      </FormRow>
      <FormRow label="Nova senha">
        <PasswordInput
          value={neu} onChange={setNeu} show={showNew} setShow={setShowNew}
          testid="settings-pw-new"
        />
        <p className="text-[11px] text-slate-400 mt-1">Mínimo de 8 caracteres.</p>
      </FormRow>
      <FormRow label="Confirme a nova senha">
        <PasswordInput
          value={confirm} onChange={setConfirm} show={showNew} setShow={setShowNew}
          testid="settings-pw-confirm"
        />
      </FormRow>
      <div className="flex justify-end pt-2">
        <SaveButton
          label="Alterar senha"
          onClick={save} loading={saving}
          disabled={!cur || !neu || !confirm}
          testid="settings-pw-save-btn"
        />
      </div>
    </Card>
  );
}

// ─── Tab: Documentos Fiscais ──────────────────────────────────────
// Card premium para gestão do perfil fiscal PJ (razão social, nome
// fantasia, endereço de faturamento) usado na emissão de NF-e. O botão
// "Atualizar via BrasilAPI" é o cerne UX: revalida em 1 clique e
// carimba `tax_profile.validated_at` para a contabilidade.

function formatCnpjMask(digits) {
  const c = String(digits || "").replace(/\D/g, "").slice(0, 14).padEnd(14, " ");
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12, 14)}`.trim();
}

function formatValidatedAt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return null;
  }
}

function FiscalTab({ tenant, onSaved }) {
  const initial = () => ({
    company_legal_name: tenant?.company_legal_name || "",
    company_trade_name: tenant?.company_trade_name || "",
    fiscal_email: tenant?.fiscal_email || "",
    municipal_registration: tenant?.municipal_registration || "",
    state_registration: tenant?.state_registration || "",
    tax_regime: tenant?.tax_regime || "",
    billing_address: {
      street:     tenant?.billing_address?.street     || "",
      number:     tenant?.billing_address?.number     || "",
      complement: tenant?.billing_address?.complement || "",
      district:   tenant?.billing_address?.district   || "",
      city:       tenant?.billing_address?.city       || "",
      state:      tenant?.billing_address?.state      || "",
      zip_code:   tenant?.billing_address?.zip_code   || "",
    },
  });
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { setForm(initial()); /* eslint-disable-next-line */ }, [tenant]);

  const source = tenant?.tax_profile?.source || "manual";
  const validatedAt = formatValidatedAt(tenant?.tax_profile?.validated_at);
  const cnpjMasked = tenant?.cnpj ? formatCnpjMask(tenant.cnpj) : "—";

  const isDirty = (() => {
    const a = tenant?.billing_address || {};
    return (
      form.company_legal_name !== (tenant?.company_legal_name || "") ||
      form.company_trade_name !== (tenant?.company_trade_name || "") ||
      form.fiscal_email !== (tenant?.fiscal_email || "") ||
      form.municipal_registration !== (tenant?.municipal_registration || "") ||
      form.state_registration !== (tenant?.state_registration || "") ||
      form.tax_regime !== (tenant?.tax_regime || "") ||
      form.billing_address.street     !== (a.street     || "") ||
      form.billing_address.number     !== (a.number     || "") ||
      form.billing_address.complement !== (a.complement || "") ||
      form.billing_address.district   !== (a.district   || "") ||
      form.billing_address.city       !== (a.city       || "") ||
      form.billing_address.state      !== (a.state      || "") ||
      form.billing_address.zip_code   !== (a.zip_code   || "")
    );
  })();

  const setAddr = (k, v) => setForm((p) => ({
    ...p, billing_address: { ...p.billing_address, [k]: v },
  }));

  const refreshFromBrasilAPI = async () => {
    if (!tenant?.cnpj) {
      toast.error("Cadastre um CNPJ na aba Empresa antes de atualizar.");
      return;
    }
    setRefreshing(true);
    try {
      const { data } = await api.post("/auth/me/tenant/fiscal-refresh");
      onSaved?.(data);
      toast.success("Dados fiscais atualizados via BrasilAPI.");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 503) {
        toast.error("BrasilAPI indisponível agora. Tente novamente em alguns minutos.");
      } else if (status === 404) {
        toast.error("CNPJ não encontrado na Receita Federal.");
      } else {
        toast.error(msg || "Não foi possível atualizar via BrasilAPI.");
      }
    } finally {
      setRefreshing(false);
    }
  };

  const save = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      const payload = {
        company_legal_name: form.company_legal_name.trim() || null,
        company_trade_name: form.company_trade_name.trim() || null,
        fiscal_email: form.fiscal_email.trim().toLowerCase() || null,
        municipal_registration: form.municipal_registration.trim() || null,
        state_registration: form.state_registration.trim() || null,
        tax_regime: form.tax_regime || null,
        billing_address: { ...form.billing_address },
      };
      // Trim address fields — sanitiza pra manter Stripe metadata limpa.
      Object.keys(payload.billing_address).forEach((k) => {
        payload.billing_address[k] = String(payload.billing_address[k] || "").trim();
      });
      const { data } = await api.patch("/auth/me/tenant", payload);
      onSaved?.(data);
      toast.success("Documentos fiscais atualizados.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const sourceBadge = source === "brasilapi" ? (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200/60"
      data-testid="fiscal-source-badge"
    >
      <ShieldCheck size={11} weight="bold" /> Validado · BrasilAPI
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium tracking-wide bg-amber-50 text-amber-700 border border-amber-200/60"
      data-testid="fiscal-source-badge"
    >
      <WarningCircle size={11} weight="bold" /> Preenchimento manual
    </span>
  );

  return (
    <section
      className="bg-white border border-slate-200 rounded-lg p-6 space-y-5"
      data-testid="fiscal-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 tracking-tight">
            Documentos Fiscais
          </h2>
          <p className="text-[12px] text-slate-500 mt-0.5 max-w-md">
            Dados oficiais usados na emissão de NF-e pela contabilidade e enviados ao Stripe.
          </p>
        </div>
        <button
          type="button"
          onClick={refreshFromBrasilAPI}
          disabled={refreshing || !tenant?.cnpj}
          data-testid="fiscal-refresh-btn"
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-300 bg-white text-slate-800 hover:border-slate-900 hover:bg-slate-50 text-[12.5px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={tenant?.cnpj ? "Refaz a consulta na BrasilAPI e atualiza razão social + endereço" : "Cadastre o CNPJ na aba Empresa primeiro"}
        >
          <ArrowsClockwise
            size={13}
            weight="bold"
            className={refreshing ? "animate-spin" : ""}
          />
          {refreshing ? "Atualizando…" : "Atualizar via BrasilAPI"}
        </button>
      </div>

      {/* Cabeçalho compacto: CNPJ + badge de procedência + validated_at */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200/70 bg-slate-50/60 px-4 py-3">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium mb-1">
            CNPJ
          </div>
          <div
            className="text-[15px] font-semibold text-slate-900 tabular-nums"
            data-testid="fiscal-cnpj-display"
          >
            {cnpjMasked}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {sourceBadge}
          {validatedAt ? (
            <span
              className="text-[10.5px] text-slate-500"
              data-testid="fiscal-validated-at"
            >
              Validado em {validatedAt}
            </span>
          ) : (
            <span className="text-[10.5px] text-slate-400">Nunca validado</span>
          )}
        </div>
      </div>

      {/* Dados da empresa */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormRow label="Razão social">
          <input
            type="text"
            value={form.company_legal_name}
            onChange={(e) => setForm({ ...form, company_legal_name: e.target.value })}
            data-testid="fiscal-legal-name-input"
            className="settings-input"
            placeholder="Ex: OFICINA BOM CAMINHO LTDA"
          />
        </FormRow>
        <FormRow label="Nome fantasia">
          <input
            type="text"
            value={form.company_trade_name}
            onChange={(e) => setForm({ ...form, company_trade_name: e.target.value })}
            data-testid="fiscal-trade-name-input"
            className="settings-input"
            placeholder="Ex: Bom Caminho"
          />
        </FormRow>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormRow label="Email fiscal (NF-e)">
          <input
            type="email"
            value={form.fiscal_email}
            onChange={(e) => setForm({ ...form, fiscal_email: e.target.value })}
            data-testid="fiscal-email-input"
            className="settings-input"
            placeholder="contabilidade@empresa.com"
          />
        </FormRow>
        <FormRow label="Inscrição Municipal">
          <input
            type="text"
            value={form.municipal_registration}
            onChange={(e) => setForm({ ...form, municipal_registration: e.target.value })}
            data-testid="fiscal-municipal-input"
            className="settings-input"
            placeholder="Opcional"
          />
        </FormRow>
        <FormRow label="Inscrição Estadual">
          <input
            type="text"
            value={form.state_registration}
            onChange={(e) => setForm({ ...form, state_registration: e.target.value })}
            data-testid="fiscal-state-registration-input"
            className="settings-input"
            placeholder="Opcional"
          />
        </FormRow>
      </div>

      {/* v0.95.17 — Regime tributário + snapshot CNAE/IBGE (readonly) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormRow label="Regime tributário">
          <select
            value={form.tax_regime}
            onChange={(e) => setForm({ ...form, tax_regime: e.target.value })}
            data-testid="fiscal-tax-regime-select"
            className="settings-input bg-white"
          >
            <option value="">Selecione…</option>
            <option value="simples_nacional">Simples Nacional</option>
            <option value="mei">MEI</option>
            <option value="lucro_presumido">Lucro Presumido</option>
            <option value="lucro_real">Lucro Real</option>
          </select>
        </FormRow>
        <FormRow label="CNAE principal">
          <input
            type="text"
            readOnly
            value={tenant?.main_cnae || "—"}
            data-testid="fiscal-main-cnae-readonly"
            className="settings-input bg-slate-50 text-slate-500 cursor-not-allowed"
            title="Preenchido automaticamente pela BrasilAPI"
          />
        </FormRow>
        <FormRow label="Código IBGE">
          <input
            type="text"
            readOnly
            value={tenant?.ibge_code || "—"}
            data-testid="fiscal-ibge-readonly"
            className="settings-input bg-slate-50 text-slate-500 cursor-not-allowed tabular-nums"
            title="Preenchido automaticamente pela BrasilAPI"
          />
        </FormRow>
      </div>

      <div className="pt-1">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium mb-2.5">
          Endereço de faturamento
        </div>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-8">
            <FormRow label="Logradouro">
              <input
                type="text"
                value={form.billing_address.street}
                onChange={(e) => setAddr("street", e.target.value)}
                data-testid="fiscal-street-input"
                className="settings-input"
                placeholder="Rua / Avenida"
              />
            </FormRow>
          </div>
          <div className="col-span-6 md:col-span-4">
            <FormRow label="Número">
              <input
                type="text"
                value={form.billing_address.number}
                onChange={(e) => setAddr("number", e.target.value)}
                data-testid="fiscal-number-input"
                className="settings-input"
                placeholder="Nº"
              />
            </FormRow>
          </div>
          <div className="col-span-12 md:col-span-6">
            <FormRow label="Complemento">
              <input
                type="text"
                value={form.billing_address.complement}
                onChange={(e) => setAddr("complement", e.target.value)}
                data-testid="fiscal-complement-input"
                className="settings-input"
                placeholder="Sala, andar, ponto de referência"
              />
            </FormRow>
          </div>
          <div className="col-span-12 md:col-span-6">
            <FormRow label="Bairro">
              <input
                type="text"
                value={form.billing_address.district}
                onChange={(e) => setAddr("district", e.target.value)}
                data-testid="fiscal-district-input"
                className="settings-input"
              />
            </FormRow>
          </div>
          <div className="col-span-8 md:col-span-4">
            <FormRow label="Cidade">
              <input
                type="text"
                value={form.billing_address.city}
                onChange={(e) => setAddr("city", e.target.value)}
                data-testid="fiscal-city-input"
                className="settings-input"
              />
            </FormRow>
          </div>
          <div className="col-span-4 md:col-span-2">
            <FormRow label="UF">
              <input
                type="text"
                value={form.billing_address.state}
                onChange={(e) => setAddr("state", e.target.value.toUpperCase().slice(0, 2))}
                data-testid="fiscal-state-input"
                className="settings-input uppercase"
                maxLength={2}
              />
            </FormRow>
          </div>
          <div className="col-span-12 md:col-span-4">
            <FormRow label="CEP">
              <input
                type="text"
                value={form.billing_address.zip_code}
                onChange={(e) => setAddr("zip_code", e.target.value.replace(/\D/g, "").slice(0, 8))}
                data-testid="fiscal-zip-input"
                className="settings-input tabular-nums"
                inputMode="numeric"
                placeholder="00000000"
              />
            </FormRow>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        <p className="text-[11px] text-slate-400 max-w-md leading-relaxed">
          O CNPJ pode ser alterado apenas na aba <span className="font-medium text-slate-600">Empresa</span> —
          mudança de CNPJ dispara reemissão de fatura no ciclo seguinte.
        </p>
        <SaveButton
          onClick={save}
          loading={saving}
          disabled={!isDirty}
          testid="fiscal-save-btn"
        />
      </div>
    </section>
  );
}

// ─── small UI pieces ──────────────────────────────────────────────

function Card({ title, desc, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 tracking-tight">{title}</h2>
        <p className="text-[12px] text-slate-500 mt-0.5">{desc}</p>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function FormRow({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function PasswordInput({ value, onChange, show, setShow, testid }) {
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="settings-input pr-10"
        placeholder="••••••••"
        autoComplete="new-password"
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-900"
        tabIndex={-1}
      >
        {show ? <EyeSlash size={14} /> : <Eye size={14} />}
      </button>
      <style>{`
        .settings-input {
          width: 100%; height: 40px;
          background: #FAFBFC; border: 1px solid #E5E9EF;
          border-radius: 8px; padding: 0 12px;
          font-size: 13.5px; color: #0F172A;
          outline: none;
          transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
        }
        .settings-input:hover:not(:disabled) { border-color: #CBD5E1; }
        .settings-input:focus { background:#FFFFFF; border-color:#0F172A; box-shadow:0 0 0 3px rgba(15,23,42,0.05); }
      `}</style>
    </div>
  );
}

function SaveButton({ onClick, loading, disabled, label = "Salvar alterações", testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testid}
      className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {loading ? <Spinner size={13} className="animate-spin" /> : <Check size={13} weight="bold" />}
      {loading ? "Salvando…" : label}
    </button>
  );
}
